/**
 * Inverted-hull outlines — the silhouette that survives on a phone.
 *
 * The post-processing outline (`toonOutlinePass`, 0.006) is a full-screen pass
 * that needs render targets a low-end device cannot spare. This is the classic
 * cheap substitute: draw the mesh a second time, slightly fattened along its
 * normals, front faces culled, unlit black. One extra draw call per character,
 * zero render targets, and it reads almost identically at gameplay distance.
 *
 * Applied to CHARACTERS ONLY. Fattening a 40-unit platform produces a black slab
 * poking through the floor, and the arena reads fine without it.
 */

import * as THREE from 'three/webgpu';
import { positionLocal, normalLocal, float, vec4, clamp, modelViewMatrix } from 'three/tsl';
import { outlineMode, onOutlineModeChange } from './PostProcessing.js';

/** Thickness in local units AT `HULL_REF_DISTANCE`. Characters are ~2 units tall. */
/**
 * How far the hull is pushed along the normal, in world units.
 *
 * Exported because the procedural bodies and the character batcher build their
 * own fatten graphs — one per-body, one per-instance — and a silhouette that
 * differs between the pooled, naive and legacy paths is the kind of thing
 * nobody notices until two players look subtly unlike each other.
 */
export const HULL_THICKNESS = 0.045;
/** View distance where HULL_THICKNESS is correct as-is. */
const HULL_REF_DISTANCE = 10;

const _hulls = new Set();

function makeHullMaterial(thickness) {
  const material = new THREE.MeshBasicNodeMaterial({ color: 0x05060d });
  // Fatten along the normal in object space. Doing it in the vertex node rather
  // than scaling the mesh keeps thin limbs from ballooning at their tips.
  //
  // Scaled by view depth: a fixed world thickness shrinks with distance, so an
  // enemy 60 units away lost the silhouette exactly where a battle royale needs
  // it most (the pipeline outline these tiers stand in for is screen-constant).
  // Clamped so close-ups never thin out and far hulls never turn into blobs.
  const viewDepth = modelViewMatrix.mul(vec4(positionLocal, 1)).z.negate();
  const depthScale = clamp(viewDepth.div(HULL_REF_DISTANCE), float(1), float(8));
  material.positionNode = positionLocal.add(normalLocal.mul(float(thickness).mul(depthScale)));
  // Back faces only: the fattened copy's front faces sit inside the real mesh
  // and would z-fight it.
  material.side = THREE.BackSide;
  material.depthWrite = true;
  return material;
}

/**
 * `noOutline` is honoured on the mesh OR any ancestor group up to `root`.
 * PlayerVisuals flags the leader crown's GROUP, not its six meshes — per-mesh
 * checking would fatten every crown point into a black spike if a hull were
 * ever attached while a crown is on.
 */
function hasNoOutlineFlag(mesh, root) {
  for (let o = mesh; o && o !== root; o = o.parent) {
    if (o.userData.noOutline) return true;
  }
  return false;
}

/**
 * Attaches a hull to every Mesh under `root`, as a sibling in the same local
 * space so it inherits animation for free.
 *
 * Idempotent: calling it twice does not double the hull.
 */
export function addHullOutline(root, thickness = HULL_THICKNESS) {
  if (root.userData.hullOutline) return root.userData.hullOutline;

  const group = new THREE.Group();
  group.name = 'hull-outline';
  // (No renderOrder here: it is per-renderable-object and Groups don't pass it
  // down — a value on the group was a silent no-op, and the hulls draw
  // correctly without one because they depth-test like any opaque mesh.)

  root.traverse((child) => {
    if (!child.isMesh || child.userData.isOutlineHull) return;
    if (hasNoOutlineFlag(child, root)) return;
    const hull = new THREE.Mesh(child.geometry, makeHullMaterial(thickness));
    hull.userData.isOutlineHull = true;
    hull.castShadow = false;
    hull.receiveShadow = false;
    hull.frustumCulled = child.frustumCulled;
    // Copy the child's transform relative to root. Children of the root are one
    // level deep in every character model, which keeps this exact.
    hull.position.copy(child.position);
    hull.quaternion.copy(child.quaternion);
    hull.scale.copy(child.scale);
    if (child.parent !== root) {
      // Deeper nesting: bake the accumulated transform once at build time.
      child.updateWorldMatrix(true, false);
      root.updateWorldMatrix(true, false);
      hull.matrixAutoUpdate = false;
      hull.matrix.copy(root.matrixWorld).invert().multiply(child.matrixWorld);
    }
    group.add(hull);
  });

  root.add(group);
  root.userData.hullOutline = group;
  _hulls.add(root);
  group.visible = outlineMode() === 'hull';
  return group;
}

export function removeHullOutline(root) {
  const group = root.userData.hullOutline;
  if (!group) return;
  for (const hull of group.children) hull.material.dispose();
  root.remove(group);
  delete root.userData.hullOutline;
  _hulls.delete(root);
}

/** Show or hide every hull at once when the quality tier flips owner. */
export function setHullOutlinesVisible(visible) {
  for (const root of _hulls) {
    const group = root.userData.hullOutline;
    if (group) group.visible = visible;
  }
}

onOutlineModeChange((mode) => setHullOutlinesVisible(mode === 'hull'));
